import { useState } from "react";
import { motion } from "framer-motion";
import { X, User, LogOut, ExternalLink, XCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import axios from "axios";
import { LoginForm, loginButtonClass } from "./LoginForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to safely extract error message from axios errors
const getErrorMessage = (err) => {
  const detail = err.response?.data?.detail;
  if (!detail) return "Hiba történt";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
  }
  if (typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return "Hiba történt";
};

// Main Auth Modal - Full functionality with registration, login, admin, forgot password
export function AuthModal({ 
  show, 
  onClose, 
  onLoginSuccess,
  initialMode = "login",
  initialStep = "auth"
}) {
  const [mode, setMode] = useState(initialMode); // login or register
  const [step, setStep] = useState(initialStep); // auth, verify, profile, forgot, reset
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [profileData, setProfileData] = useState({ birth_year: "", birth_month: "", birth_day: "", phone: "" });
  const [identifier, setIdentifier] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminError, setAdminError] = useState("");
  const [adminLoading, setAdminLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  
  const inputClass = "bg-slate-50 border-slate-200 text-slate-800 rounded-xl h-11 focus:border-[#F97316] focus:ring-[#F97316]/20";

  if (!show) return null;

  const handleAdminLogin = async () => {
    if (!adminPassword) {
      setAdminError("Add meg a jelszót!");
      return;
    }
    setAdminLoading(true);
    setAdminError("");
    try {
      const response = await axios.post(`${API}/competition/admin-login?password=${encodeURIComponent(adminPassword)}`);
      const adminUser = response.data.user;
      const adminToken = response.data.token;
      
      localStorage.setItem("comp_token", adminToken);
      localStorage.setItem("comp_admin", "true");
      localStorage.setItem("comp_user", JSON.stringify(adminUser));
      toast.success("Admin bejelentkezés sikeres!");
      setAdminPassword("");
      onLoginSuccess?.(adminUser, adminToken);
      onClose();
      // NO redirect - stay on current page
    } catch (err) {
      setAdminError("Hibás jelszó!");
      toast.error(getErrorMessage(err));
    }
    setAdminLoading(false);
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const res = await axios.post(`${API}/competition/register`, {
        name: form.name,
        email: form.email,
        password: form.password
      });
      if (res.data.requires_verification) {
        setVerifyEmail(form.email);
        setStep("verify");
        toast.success("Ellenőrző kód elküldve emailben!");
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
    setAuthLoading(false);
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    setVerifyLoading(true);
    try {
      const res = await axios.post(`${API}/competition/verify`, {
        email: verifyEmail,
        code: verifyCode
      });
      setIdentifier(res.data.identifier);
      setTempToken(res.data.token);
      setStep("profile");
      toast.success("Email ellenőrizve!");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
    setVerifyLoading(false);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/competition/complete-profile?token=${tempToken}`, {
        birth_year: parseInt(profileData.birth_year),
        birth_month: parseInt(profileData.birth_month),
        birth_day: parseInt(profileData.birth_day),
        phone: profileData.phone || ""
      });
      if (res.data.pending_approval) {
        toast.success(res.data.message || "Regisztráció beküldve – a belépés az admin jóváhagyása után lehetséges.", { duration: 8000 });
        onClose();
        return;
      }
      localStorage.setItem("comp_token", tempToken);
      localStorage.setItem("comp_user", JSON.stringify(res.data.user || { name: form.name, email: form.email }));
      toast.success("Regisztráció befejezve!");
      onLoginSuccess?.(res.data.user || { name: form.name, email: form.email }, tempToken);
      onClose();
      // NO redirect - stay on current page
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    try {
      await axios.post(`${API}/competition/resend-code`, { email: verifyEmail });
      toast.success("Új kód elküldve!");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
    setResendLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/competition/forgot-password`, { email: forgotEmail });
      toast.success("Ha létezik ilyen email, elküldtük a visszaállítási kódot.");
      setStep("reset");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
    setForgotLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("A jelszónak legalább 6 karakter hosszúnak kell lennie!");
      return;
    }
    setResetLoading(true);
    try {
      await axios.post(`${API}/competition/reset-password`, { 
        email: forgotEmail, 
        code: resetCode, 
        new_password: newPassword 
      });
      toast.success("Jelszó sikeresen megváltoztatva!");
      setStep("auth");
      setForgotEmail("");
      setResetCode("");
      setNewPassword("");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
    setResetLoading(false);
  };

  const resetModal = () => {
    setStep("auth");
    setMode("login");
    setForm({ email: "", password: "", name: "" });
    setVerifyCode("");
    setVerifyEmail("");
    setProfileData({ birth_year: "", birth_month: "", birth_day: "" });
    setIdentifier("");
    setTempToken("");
    setForgotEmail("");
    setResetCode("");
    setNewPassword("");
    setAdminPassword("");
    setAdminError("");
  };
  
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-slate-800 font-bold text-xl">
            {step === "auth" && (mode === "login" ? "Bejelentkezés" : "Regisztráció")}
            {step === "verify" && "Email ellenőrzés"}
            {step === "profile" && "Adatok megadása"}
            {step === "forgot" && "Elfelejtett jelszó"}
            {step === "reset" && "Jelszó visszaállítás"}
          </h3>
          <button onClick={() => { resetModal(); onClose(); }} className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded-lg transition-all">
            <X size={22} />
          </button>
        </div>

        {/* Step 1: Auth (Login or Register) */}
        {step === "auth" && mode === "login" && (
          <LoginForm
            testIdPrefix="modal-login"
            onSuccess={(user, token) => { onLoginSuccess?.(user, token); onClose(); }}
            onRegister={() => setMode("register")}
            onForgot={() => setStep("forgot")}
          />
        )}

        {step === "auth" && mode === "register" && (
          <>
            <form onSubmit={handleRegisterSubmit} className="space-y-4">
              <div>
                <label className="text-slate-600 text-sm mb-1.5 block font-medium">Név</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputClass}
                  placeholder="Teljes név"
                  required
                  data-testid="auth-name-input"
                />
              </div>
              
              <div>
                <label className="text-slate-600 text-sm mb-1.5 block font-medium">E-mail-cím</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputClass}
                  placeholder="pelda@email.hu"
                  required
                  data-testid="auth-email-input"
                />
              </div>
              
              <div>
                <label className="text-slate-600 text-sm mb-1.5 block font-medium">Jelszó</label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className={inputClass}
                  placeholder="••••••••"
                  required
                  data-testid="auth-password-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className={loginButtonClass}
                disabled={authLoading}
                data-testid="auth-submit-btn"
              >
                {authLoading ? "Betöltés..." : "Regisztráció"}
              </Button>
            </form>
            
            <p className="mt-4 text-center text-slate-500 text-sm">
              Van már fiókod? <button onClick={() => setMode("login")} className="text-[#EA6C0A] hover:text-[#C2410C] font-semibold" data-testid="switch-to-login">Jelentkezz be!</button>
            </p>
          </>
        )}

        {/* Step 2: Verify Email */}
        {step === "verify" && (
          <form onSubmit={handleVerifySubmit} className="space-y-4">
            <p className="text-slate-500 text-sm">
              Küldtünk egy 6 számjegyű kódot a <span className="text-[#EA6C0A] font-medium">{verifyEmail}</span> címre.
            </p>
            
            <div>
              <label className="text-slate-600 text-sm mb-1.5 block font-medium">Ellenőrző kód</label>
              <Input
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="bg-slate-50 border-slate-200 text-slate-800 text-center text-2xl tracking-widest focus:border-[#F97316]"
                placeholder="000000"
                maxLength={6}
                required
                data-testid="verify-code-input"
              />
            </div>
            
            <Button type="submit" className={loginButtonClass} disabled={verifyLoading || verifyCode.length !== 6}>
              {verifyLoading ? "Ellenőrzés..." : "Ellenőrzés"}
            </Button>
            
            <div className="text-center pt-2">
              <button 
                type="button"
                onClick={handleResendCode}
                disabled={resendLoading}
                className="text-slate-400 hover:text-[#EA6C0A] text-sm disabled:opacity-50 transition-colors"
              >
                {resendLoading ? "Küldés..." : "Kód újraküldése"}
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Complete Profile */}
        {step === "profile" && (
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div>
              <label className="text-slate-600 text-sm mb-1.5 block font-medium">Azonosító</label>
              <Input
                value={identifier}
                className="bg-slate-100 border-slate-200 text-slate-800 font-mono text-lg"
                disabled
              />
              <p className="text-slate-400 text-xs mt-1">Ez az egyedi azonosítód, nem módosítható.</p>
            </div>
            
            <div>
              <label className="text-slate-600 text-sm mb-1.5 block font-medium">Születési dátum</label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={profileData.birth_year}
                  onChange={(e) => setProfileData({ ...profileData, birth_year: e.target.value })}
                  className="bg-slate-50 border-slate-200 text-slate-800 flex-1 focus:border-[#F97316]"
                  placeholder="Év"
                  min="1900"
                  max={new Date().getFullYear()}
                  required
                />
                <Input
                  type="number"
                  value={profileData.birth_month}
                  onChange={(e) => setProfileData({ ...profileData, birth_month: e.target.value })}
                  className="bg-slate-50 border-slate-200 text-slate-800 w-20 focus:border-[#F97316]"
                  placeholder="Hó"
                  min="1"
                  max="12"
                  required
                />
                <Input
                  type="number"
                  value={profileData.birth_day}
                  onChange={(e) => setProfileData({ ...profileData, birth_day: e.target.value })}
                  className="bg-slate-50 border-slate-200 text-slate-800 w-20 focus:border-[#F97316]"
                  placeholder="Nap"
                  min="1"
                  max="31"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="text-slate-600 text-sm mb-1.5 block font-medium">Telefonszám</label>
              <Input
                type="tel"
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                className="bg-slate-50 border-slate-200 text-slate-800 focus:border-[#F97316]"
                placeholder="+36 30 123 4567"
              />
            </div>
            
            <Button type="submit" className={loginButtonClass}>
              Regisztráció befejezése
            </Button>
          </form>
        )}

        {/* Step: Forgot Password */}
        {step === "forgot" && (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <p className="text-slate-500 text-sm">
              Add meg az email címedet, és küldünk egy visszaállítási kódot.
            </p>
            
            <div>
              <label className="text-slate-600 text-sm mb-1.5 block font-medium">Email cím</label>
              <Input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="bg-slate-50 border-slate-200 text-slate-800 focus:border-[#F97316]"
                placeholder="email@example.com"
                required
              />
            </div>
            
            <Button type="submit" className={loginButtonClass} disabled={forgotLoading}>
              {forgotLoading ? "Küldés..." : "Kód küldése"}
            </Button>
            
            <p className="text-center pt-2">
              <button 
                type="button"
                onClick={() => setStep("auth")} 
                className="text-slate-400 hover:text-[#EA6C0A] text-sm transition-colors"
              >
                Vissza a bejelentkezéshez
              </button>
            </p>
          </form>
        )}

        {/* Step: Reset Password */}
        {step === "reset" && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <p className="text-slate-500 text-sm">
              Küldtünk egy 6 számjegyű kódot a <span className="text-[#EA6C0A] font-medium">{forgotEmail}</span> címre.
            </p>
            
            <div>
              <label className="text-slate-600 text-sm mb-1.5 block font-medium">Visszaállítási kód</label>
              <Input
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="bg-slate-50 border-slate-200 text-slate-800 text-center text-2xl tracking-widest focus:border-[#F97316]"
                placeholder="000000"
                maxLength={6}
                required
              />
            </div>
            
            <div>
              <label className="text-slate-600 text-sm mb-1.5 block font-medium">Új jelszó</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-slate-50 border-slate-200 text-slate-800 focus:border-[#F97316]"
                placeholder="Legalább 6 karakter"
                minLength={6}
                required
              />
            </div>
            
            <Button type="submit" className={loginButtonClass} disabled={resetLoading || resetCode.length !== 6}>
              {resetLoading ? "Mentés..." : "Jelszó megváltoztatása"}
            </Button>
            
            <p className="text-center pt-2">
              <button 
                type="button"
                onClick={() => setStep("forgot")} 
                className="text-slate-400 hover:text-[#EA6C0A] text-sm transition-colors"
              >
                Kód újraküldése
              </button>
            </p>
          </form>
        )}
      </motion.div>
    </div>
  );
}

// Profile Redirect Modal - Shows when logged in user clicks profile (redirects to /tag)
export function ProfileRedirectModal({ show, onClose, user, onLogout }) {
  if (!show || !user) return null;
  
  // Store current path before navigating to tag
  const handleGoToTag = () => {
    localStorage.setItem("returnPath", window.location.pathname);
    window.location.href = "/ugyfelkapu";
  };
  
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl max-w-md w-full p-6 text-center shadow-2xl"
      >
        <div className="flex justify-end mb-2">
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded-lg transition-all">
            <X size={22} />
          </button>
        </div>
        
        <div className="w-20 h-20 bg-gradient-to-br from-[#F97316] to-[#FB923C] rounded-full flex items-center justify-center mx-auto mb-4">
          <User className="w-10 h-10 text-white" />
        </div>
        
        <h3 className="text-slate-800 font-bold text-xl mb-2">
          Szia, {user.name}!
        </h3>
        
        <p className="text-slate-500 mb-6">
          A profilod és adataid megtekintéséhez menj az Ügyfélkapura.
        </p>
        
        <div className="space-y-3">
          <Button 
            onClick={handleGoToTag}
            className={loginButtonClass}
            data-testid="go-to-tag-btn"
          >
            <ExternalLink size={18} className="mr-2" />
            Ügyfélkapu
          </Button>
          
          <Button 
            onClick={onClose}
            variant="outline"
            className="w-full border-slate-200 text-slate-600 hover:bg-slate-50"
            data-testid="cancel-btn"
          >
            <XCircle size={18} className="mr-2" />
            Mégse
          </Button>
          
          <Button 
            onClick={onLogout}
            variant="outline"
            className="w-full border-red-200 text-red-500 hover:bg-red-50"
            data-testid="logout-btn"
          >
            <LogOut size={18} className="mr-2" />
            Kijelentkezés
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

export default AuthModal;
